/**
 * A registration case's follow-up sequence: one row per scheduled message,
 * with a written status badge — including the SMS-specific "Passerelle à
 * brancher" case, since the gateway is not wired up until L6
 * (`docs/API.md` §7) — plus, since L6, a plain-language explanation of what
 * happened (`replied`/`cancelled`/`failed`) and, for a commune admin only,
 * the "Réarmer" action on a failed send (`docs/FOLLOWUP.md` §5).
 */
import { useState } from 'react';

import { rearmFollowupMessage } from '../../../api/household';
import { ApiError } from '../../../api/client';
import { Badge, Button } from '../../../components/ui';
import type { BadgeTone } from '../../../components/ui';
import { formatShortDate, truncate } from '../format';
import { t } from '../../../i18n/fr';
import { MESSAGE_STATUS_LABEL_KEYS, STEP_LABEL_KEYS } from '../labels';

import type { FollowupStatus, RegistrationCase } from '../../../api/types';
import type { MessageKey } from '../../../i18n/fr';

/**
 * One row of `RegistrationCase.messages`. `RegistrationCase` only makes its
 * own top-level fields required — nested arrays keep the raw, partially
 * optional schema shape, so `channel` and `status` are read with the same
 * defaults the backend applies (`docs/API.md` §7: `channel` defaults to
 * `email`, a freshly-scheduled message's `status` defaults to `scheduled`).
 */
export type CaseMessage = RegistrationCase['messages'][number];

export interface MailSequenceProps {
  messages: CaseMessage[];
  /**
   * The case these messages belong to. Only needed when `canRearm` is set
   * — the door confirmation screen (`RegistrationDoorPage`) shows a
   * freshly-opened case's sequence, always `scheduled`, with no rearm to
   * offer.
   */
  caseId?: number;
  /** Whether to offer "Réarmer" on a `failed` message: a commune admin only (`docs/FOLLOWUP.md` §5). */
  canRearm?: boolean;
  /** Called with the server's fresh copy of a message once "Réarmer" succeeds. */
  onMessageRearmed?: (message: CaseMessage) => void;
}

const STATUS_TONE: Record<FollowupStatus, BadgeTone> = {
  scheduled: 'yellow',
  sent: 'blue',
  opened: 'green',
  replied: 'green',
  unsubscribed: 'red',
  failed: 'red',
  cancelled: 'neutral',
};

const MAX_ERROR_LENGTH = 120;

/**
 * Render one follow-up message's badge: the SMS/cancelled combination reads
 * "Passerelle à brancher" instead of the generic "Annulé", since that is
 * the intent the volunteer needs to see (`docs/API.md` §7).
 *
 * @param message - The follow-up message row.
 * @returns The status badge element.
 */
function StatusBadge({ message }: { message: CaseMessage }) {
  const status = message.status ?? 'scheduled';
  if (message.channel === 'sms' && status === 'cancelled') {
    return <Badge tone="neutral">{t('household.cases.smsGatewayBadge')}</Badge>;
  }
  return <Badge tone={STATUS_TONE[status]}>{t(MESSAGE_STATUS_LABEL_KEYS[status])}</Badge>;
}

/**
 * The message key for what a `replied` message's `note` says the person
 * did (`docs/FOLLOWUP.md` §5).
 *
 * @param note - The message's trace.
 * @returns The detail message key; a generic "a répondu" for any `note`
 *   this screen does not recognise.
 */
function repliedDetailKey(note: string): MessageKey {
  if (note === 'replied:filed') return 'household.cases.repliedDetail.filed';
  if (note === 'replied:registered') return 'household.cases.repliedDetail.registered';
  return 'household.cases.repliedDetail.generic';
}

/**
 * The message key explaining why a `cancelled` message's sequence stopped,
 * when `note` carries one of the two recognised traces the volunteer's
 * screen explains (`docs/FOLLOWUP.md` §1, §2, §5): the case was advanced by
 * hand, or the person unsubscribed. Any other `note` (e.g. a reply's own
 * "replied:filed" echoed onto the messages it cancelled) gets no extra
 * line — the plain "Annulé" badge already says enough.
 *
 * @param note - The message's trace.
 * @returns The detail message key, or `undefined` for an unrecognised `note`.
 */
function cancelledReasonKey(note: string): MessageKey | undefined {
  if (note === 'unsubscribed') return 'household.cases.cancelledReason.unsubscribed';
  if (note.startsWith('advanced:')) {
    const stage = note.slice('advanced:'.length);
    if (stage === 'filed') return 'household.cases.cancelledReason.filed';
    if (stage === 'confirmed') return 'household.cases.cancelledReason.confirmed';
    if (stage === 'closed') return 'household.cases.cancelledReason.closed';
  }
  return undefined;
}

/**
 * Render the extra, written explanation a message's status carries beyond
 * its badge: what the person did on reply, why the sequence stopped on
 * cancellation, or the raw technical error on a failed send
 * (`docs/FOLLOWUP.md` §5). Renders nothing for a status with no such detail.
 *
 * @param props - The follow-up message row.
 * @returns The detail element(s), or `null`.
 */
function MessageDetail({ message }: { message: CaseMessage }) {
  const status = message.status ?? 'scheduled';
  const note = message.note;

  if (status === 'replied') {
    return <span className="detail">{t(repliedDetailKey(note))}</span>;
  }
  if (status === 'unsubscribed') {
    return <span className="detail">{t('household.cases.unsubscribedDetail')}</span>;
  }
  if (status === 'cancelled') {
    const key = cancelledReasonKey(note);
    return key ? <span className="detail">{t(key)}</span> : null;
  }
  if (status === 'failed') {
    return (
      <>
        <span className="detail">{t('household.cases.failedDetail')}</span>
        {note ? <span className="erreur-tech">{truncate(note, MAX_ERROR_LENGTH)}</span> : null}
      </>
    );
  }
  return null;
}

interface RearmButtonProps {
  caseId: number;
  message: CaseMessage;
  onRearmed: (message: CaseMessage) => void;
}

/**
 * "Réarmer" on a failed message: resends it immediately, shown to a
 * commune admin only. Its own busy/error state, since several rows can
 * carry the button at once (`docs/FOLLOWUP.md` §5).
 *
 * @param props - The case and message to rearm, and the success callback.
 * @returns The button element, with an inline error under a `409`.
 */
function RearmButton({ caseId, message, onRearmed }: RearmButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setBusy(true);
    setError(null);
    try {
      const rearmed = await rearmFollowupMessage(caseId, message.id);
      onRearmed(rearmed);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) {
        setError(t('household.cases.rearm.conflict'));
      } else {
        setError(t('household.cases.rearm.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rearm">
      <Button variant="secondary" disabled={busy} onClick={() => void handleClick()}>
        {t('household.cases.rearm.button')}
      </Button>
      {error ? (
        <span className="rearm-erreur" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Render a registration case's follow-up sequence.
 *
 * @param props - The case's follow-up messages, and — for "Réarmer" — the
 *   case id, whether the caller may rearm, and the success callback.
 * @returns The sequence element, or nothing for an empty sequence.
 */
export function MailSequence({
  messages,
  caseId,
  canRearm = false,
  onMessageRearmed,
}: MailSequenceProps) {
  if (messages.length === 0) {
    return <p className="mail">{t('household.cases.noEmail')}</p>;
  }
  return (
    <div>
      {messages.map((message) => (
        <div className="mail" key={message.id}>
          <span className="quand">{t(STEP_LABEL_KEYS[message.step])}</span>
          <div className="quoi">
            <div className="quoi-ligne">
              {t('household.cases.messageDate', { date: formatShortDate(message.scheduled_for) })}{' '}
              <StatusBadge message={message} />
            </div>
            <MessageDetail message={message} />
            {message.status === 'failed' && canRearm && caseId !== undefined && onMessageRearmed ? (
              <RearmButton caseId={caseId} message={message} onRearmed={onMessageRearmed} />
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}
